import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmModal } from '@microservices/wiskey-react-components'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { Box, Typography } from '@mui/material'
import { DataGridProps } from '@mui/x-data-grid'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { generateDataGridActionsColumn } from '@helpers'
import { FLEXIBLE } from '@constants'

import { CopyPrintFormDialog } from './components/CopyPrintFormDialog'
import { usePrintFormsConfig } from './hooks'

export const PrintFormsConfig = () => {
  const { t } = useTranslation()

  const {
    state: { configs, idToDelete, showCopyDialog, copyId, copyTitle },
    handlers: {
      handleCreate,
      handleEdit,
      handleShowDeleteModal,
      handleCloseDeleteModal,
      handleDelete,
      handleShowCopyDialog,
      handleCloseCopyDialog,
      handleCopy,
    },
  } = usePrintFormsConfig()

  const columns = useMemo(
    () =>
      [
        {
          field: 'title',
          headerName: t('printForms.titleField'),
          flex: 1,
        },
      ] as DataGridProps['columns'],
    [t]
  )

  const columnsWithActions = useMemo(
    () =>
      [
        ...columns,
        generateDataGridActionsColumn({
          onEdit: params => handleEdit(params.row.id as string),
          onCopy: params =>
            handleShowCopyDialog(params.row.id as string, params.row.title as string),
          onDelete: params => handleShowDeleteModal(params.row.id as string),
        }),
      ].filter(Boolean) as DataGridProps['columns'],
    [columns, handleEdit, handleShowCopyDialog, handleShowDeleteModal]
  )

  const existingIds = configs.map(c => c.id)

  return (
    <>
      {Boolean(idToDelete) && (
        <ConfirmModal
          isShow
          actionBtnText={t('modal.delete.btn')}
          containerStyle={{ px: 0.5, py: 0.5 }}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDelete}
        />
      )}
      {showCopyDialog && copyId && (
        <CopyPrintFormDialog
          existingIds={existingIds}
          isShow={showCopyDialog}
          sourceTitle={copyTitle}
          onClose={handleCloseCopyDialog}
          onCopy={handleCopy}
        />
      )}
      <PageTitle
        end={<Box sx={{ py: 0.5 }} />}
        start={
          <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
            {t('pageTitle.printForms')}
          </Typography>
        }
      />
      <PageContentLayout sx={{ ...FLEXIBLE, p: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Button sx={{ width: 150 }} variant='contained' onClick={handleCreate}>
            {t('printForms.add')}
          </Button>
        </Box>
        <DataGrid
          hideFooter
          columns={columnsWithActions}
          rows={configs}
          sx={FLEXIBLE}
          onDelete={params => handleShowDeleteModal(params as unknown as string)}
        />
      </PageContentLayout>
    </>
  )
}
